import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ScrBaseTabComponent } from 'src/app/components/base.component';
import { BillingOrder } from 'src/app/models/billingorder.model';
import { OrderType } from 'src/app/models/constants';
import { Lease } from 'src/app/models/lease.model';
import { Config, ConfigService } from 'src/app/services/config.service';
import { GetOrderResponse, ShortCodesService } from 'src/app/services/shortcodes.service';
import { cloneDeep } from 'lodash-es';

@Component({
  templateUrl: './invoice.component.html',
  styleUrls: ['./invoice.component.scss']
})
export class InvoiceComponent extends ScrBaseTabComponent implements OnInit {

  public orderId: number;
  public config?: Config;
  public order?: BillingOrder;
  public displayOnlyOrder?: BillingOrder; //New object for display purpose - renew1plus12 needs a special shift twist
  public hasDiscount = false;
  public poNumber?: string;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private shortCodesService: ShortCodesService,
    private configService: ConfigService
  ) {
    super();
    this.tt = 'Invoice';
    this.routeElement = 'invoice';

    this.orderId = this.route.snapshot.params.orderId;
  }

  override getObjectId(): string {
    return `${this.orderId}`;
  }

  ngOnInit(): void {
    this.subscriptions.push(this.route.params.subscribe({
      next: (params) => {
        this.log.trace('params are now ' + JSON.stringify(params));
        this.orderId = params.orderId;
        this.fetchOrderAndConfig();
      }
    }));
  }

  fetchOrderAndConfig(): void {

    this.observe2<Config, GetOrderResponse>(this.configService.getConfig(), this.shortCodesService.getOrder(this.orderId),
      ((r1, r2) => {
        this.config = r1;
        this.order = r2.order;
        if (this.order.poNumber)
          this.poNumber = this.order.poNumber;
        else
          this.poNumber = '';
        if (r2.renewalDeadLineStr)
          this.order.renewalDeadLineStr = r2.renewalDeadLineStr;
        if (r2.renewalAfterDeadline)
          this.order.renewalAfterDeadline = r2.renewalAfterDeadline;
        if (r2.monBillStartDateStr)
          this.order.monBillStartDateStr = r2.monBillStartDateStr;
        if (r2.allMBDiscountsNotApplied)
          this.order.allMBDiscountsNotApplied = r2.allMBDiscountsNotApplied;
        this.hasDiscount = this.order.leases.some(l => (l.discountAmount && l.discountAmount !== 0) || (l.discountList && l.discountList.length > 0));

        this.displayOnlyOrder = cloneDeep(this.order);
        this.processRenew1plus12Display();
        return false;//no success message.
      }));
  }

  jumpToCheckOrWire() {
    document.getElementById('check-or-wire')?.scrollIntoView();
  }

  printInvoice() {
    this.clearMessages();
    const po = this.createPrintPartialObserver('application/pdf');
    this.shortCodesService.getInvoicePdf(this.orderId).subscribe(po);
  }

  downloadPdf() {
    this.clearMessages();
    const po = this.createDownloadPartialObserver(
      'application/pdf',
      this.order?.orderType == 'Prepay'
        ? `ShortCode_Invoice_${this.orderId}.pdf`
        : this.order?.orderType == 'BilledMonthly'
          ? `ShortCode_MonthlyInvoice_${this.orderId}.pdf`
          : `ShortCode_Invoice_${this.orderId}.pdf`);

    this.shortCodesService.getInvoicePdf(this.orderId).subscribe(po);
  }

  payWithCard(): void {
    this.clearMessages();
    this.log.debug('pay with card invoked');
    this.log.trace(JSON.stringify(this.order));
    if (this.order?.orderType && this.order?.orderType === OrderType.Prepay && this.order?.applicationType && this.order?.applicationType == 'New') {
      this.router.navigate(
        ['/dashboard',
          {
            outlets: {
              'navbar-tab':
                ['order-code',
                  { outlets: { 'order-code-tab': ['payment'] } }
                ]
            }
          }
        ], { state: { payLater: true, order: this.order } });
    } else if (this.order?.orderType && this.order?.orderType === OrderType.Prepay && this.order?.applicationType && this.order?.applicationType == 'Renew') {

      this.router.navigate(
        ['/dashboard',
          {
            outlets: {
              'navbar-tab':
                ['order-code',
                  { outlets: { 'order-code-tab': ['payment'] } }
                ]
            }
          }
        ], { state: { payLater: true, renew: true, order: this.order } });
    } else if (this.order?.orderType && this.order?.orderType === OrderType.BilledMonthly) {
      this.router.navigate(
        ['/dashboard',
          {
            outlets: {
              'navbar-tab':
                ['order-code',
                  { outlets: { 'order-code-tab': ['payment'] } }
                ]
            }
          }
        ], { state: { manualPay: true, order: this.order } });
    }
  }

  viewReceipt(): void {
    this.clearMessages();

    this.router.navigate([
      '/dashboard',
      {
        outlets: {
          'navbar-tab': ['my-shortcodes', {
            outlets: {
              'my-shortcode-tab': this.order?.paymentId ? ['multiPaymentReceipt', this.order.paymentId] : ['receipt', this.orderId]
            }
          }]
        }
      }
    ]).then(() => {
      window.scrollTo(0, 0);
    });

  }


  joinedDiscounts(item: Lease): string {
    if (item.discountList) {
      return item.discountList.map(d => d.displayName).join(', ');
    }
    else {
      return '';
    }
  }

  updatePurchaseOrder() {
    this.clearMessages();

    if (this.poNumber) {
      this.observe(this.shortCodesService.updatePurchaseOrder(this.orderId, this.poNumber || ''), () => {
        this.ngOnInit();
      });
    }
  }

  removePurchaseOrder() {
    this.clearMessages();
    this.observe(this.shortCodesService.updatePurchaseOrder(this.orderId, ''), () => {
      this.ngOnInit();
    });
  }

  
  processRenew1plus12Display() {
    if (this.order && this.displayOnlyOrder &&
      this.order.applicationType && this.order.applicationType === 'Renew' &&
      this.order.leases && this.order.leases.length == 2 &&
      this.order.leases[1].term == 1 &&
      this.order.leases[0].leaseType === 'BilledMonthly') {
      this.displayOnlyOrder.totalPrice = this.order.leases[1].price ? this.order.leases[1].price : 0;
      this.displayOnlyOrder.leases.shift();
    }
  }
}